/**
 * Address Form Schema Generator
 *
 * Core utility for generating address form schemas based on country and options.
 * This is the main entry point for consuming applications.
 */

import { getCountryAddressFormat } from './countries';

import type { AddressFieldSchema, AddressFormSchema, AddressFormSchemaOptions, ValidationRule } from './types';

/**
 * Generate address form schema for a specific country with options
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'CA')
 * @param {AddressFormSchemaOptions} options - Configuration options for the form
 * @returns {AddressFormSchema} Complete address form schema ready for rendering
 * @example
 * ```ts
 * const schema = generateAddressFormSchema('US', {
 *   addressType: 'shipTo',
 *   hiddenFields: ['line3'],
 *   requiredFields: ['phone'],
 *   showAddressLine3: false
 * });
 * ```
 */
export const generateAddressFormSchema = (countryCode: string, options: AddressFormSchemaOptions = {}): AddressFormSchema => {
  const { addressType = 'shipTo', hiddenFields = [], requiredFields = [], requireAll = false, showAddressLine3 = true } = options;

  // Get base country format
  const countryFormat = getCountryAddressFormat(countryCode);

  // Clone fields to avoid mutation
  let fields = countryFormat.fields.map((field) => ({ ...field }));

  // Filter hidden fields
  fields = filterHiddenFields(fields, hiddenFields);

  // Handle line3 visibility
  if (!showAddressLine3) {
    fields = fields.filter((field) => field.id !== 'line3');
  }

  // Apply required field overrides
  fields = applyRequiredFieldOverrides(fields, requiredFields, requireAll);

  return {
    countryCode: countryFormat.countryCode,
    addressType,
    fields,
  };
};

/**
 * Filter out hidden fields from schema
 * @param {AddressFieldSchema[]} fields - Array of field schemas
 * @param {string[]} hiddenFields - Array of field IDs to hide
 * @returns {AddressFieldSchema[]} Filtered fields
 */
const filterHiddenFields = (fields: AddressFieldSchema[], hiddenFields: string[]): AddressFieldSchema[] => {
  if (hiddenFields.length === 0) return fields;
  return fields.filter((field) => !hiddenFields.includes(field.id));
};

/**
 * Apply required field overrides to schema
 *
 * If requireAll is true, all fields become required.
 * If requiredFields is provided, those specific fields become required.
 * Otherwise, uses default required status from field definition.
 * @param {AddressFieldSchema[]} fields - Array of field schemas
 * @param {string[]} requiredFields - Array of field IDs that should be required
 * @param {boolean} requireAll - Make all fields required
 * @returns {AddressFieldSchema[]} Fields with updated validation rules
 */
const applyRequiredFieldOverrides = (fields: AddressFieldSchema[], requiredFields: string[], requireAll: boolean): AddressFieldSchema[] => {
  return fields.map((field) => {
    const shouldBeRequired = determineIfFieldRequired(field, requiredFields, requireAll);

    // Update validation rules
    const updatedValidation = updateRequiredValidation(field.validation, shouldBeRequired);

    return {
      ...field,
      validation: updatedValidation,
    };
  });
};

/**
 * Determine if a field should be required based on options
 * @param {AddressFieldSchema} field - Field schema
 * @param {string[]} requiredFields - Explicit required field IDs
 * @param {boolean} requireAll - Require all fields flag
 * @returns {boolean} Whether field should be required
 */
const determineIfFieldRequired = (field: AddressFieldSchema, requiredFields: string[], requireAll: boolean): boolean => {
  // If requireAll is true, everything is required
  if (requireAll) return true;

  // If requiredFields is specified, check if this field is in the list
  if (requiredFields.length > 0) {
    return requiredFields.includes(field.name);
  }

  // Otherwise, use default required status
  return field.defaultRequired ?? false;
};

/**
 * Update validation rules to add or remove required rule
 * @param {ValidationRule[]} validation - Current validation rules
 * @param {boolean} isRequired - Whether field should be required
 * @returns {ValidationRule[]} Updated validation rules
 */
const updateRequiredValidation = (validation: ValidationRule[], isRequired: boolean): ValidationRule[] => {
  // Remove existing required rule
  const withoutRequired = validation.filter((rule) => rule.type !== 'required');

  // Add required rule if needed
  if (isRequired) {
    return [{ type: 'required', value: true }, ...withoutRequired];
  }

  return withoutRequired;
};

/**
 * Group inline fields for rendering
 *
 * Processes fields array and groups consecutive inline fields together.
 * This allows renderers to display fields side-by-side (e.g., city, state, zip).
 * @param {AddressFieldSchema[]} fields - Array of field schemas
 * @returns {(AddressFieldSchema | AddressFieldSchema[])[]} Fields grouped by inline layout hint
 * @example
 * ```ts
 * const grouped = groupInlineFields(schema.fields);
 * // Returns: [
 * //   [nameField],
 * //   [companyField],
 * //   [line1Field],
 * //   [cityField, stateField, zipField], // These are inline
 * //   [phoneField]
 * // ]
 * ```
 */
export const groupInlineFields = (fields: AddressFieldSchema[]): (AddressFieldSchema | AddressFieldSchema[])[] => {
  const result: (AddressFieldSchema | AddressFieldSchema[])[] = [];

  for (const field of fields) {
    if (field.layout.isInline) {
      // Check if previous element is an array (inline group)
      const lastElement = result[result.length - 1];
      if (Array.isArray(lastElement)) {
        // Add to existing inline group
        lastElement.push(field);
      } else {
        // Start new inline group
        result.push([field]);
      }
    } else {
      // Non-inline field - add as single element
      result.push(field);
    }
  }

  return result;
};

/**
 * Check if a field is required in the schema
 *
 * Utility function for consumers to check required status.
 * @param {AddressFieldSchema} field - Field schema
 * @returns {boolean} Whether field has required validation rule
 */
export const isFieldRequired = (field: AddressFieldSchema): boolean => {
  return field.validation.some((rule) => rule.type === 'required' && rule.value === true);
};
